import { useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
// import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { DEFAULT_WELCOME_MESSAGE, DEFAULT_UNAVAILABLE_MESSAGE } from 'constants/data';
import { CheckboxCheckedIcon, CheckboxIcon } from 'assets';
import { FormInput, FormMediaUpload, FormSelector, Info } from 'components';
import { ApiUser, UpdateClinicUserRequest, UpdateUserRequest } from 'types';
import { useRegistration } from 'hooks';
import { IRegistrationContext } from 'context';
import { selectUser } from 'store/user';
import { useAppSelector } from 'store';
import { getSuffixesOptionsForUser, getTitlesOptionsForUser, isPreferredDisplayNameValid } from 'utils';
import { isHipaaLink } from '../../../../config/productProfile';

import styles from './CreateRoomForm.module.scss';

type CreateWaitingRoomFormType = {
  user: UpdateUserRequest;
  clinicUser: UpdateClinicUserRequest;
};

export function CreateRoomForm() {
  const { t } = useTranslation('auth');
  const { enqueueSnackbar } = useSnackbar();
  // const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { onCreateRoomFormSkip: onSkip, onCreateRoomFormSubmit: onSubmit }: IRegistrationContext = useRegistration();
  const user: ApiUser | null = useAppSelector(selectUser);
  const titleSuffixMenuProps = {
    PaperProps: {
      sx: (theme: Theme) => ({
        '& .MuiMenuItem-root:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, 0.14),
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
        },
      }),
    },
  };

  const schema = Yup.object().shape({
    clinicUser: Yup.object().shape({
      roomName: Yup.string()
        .required(t('validationRoomUrlRequired'))
        .matches(/^(([a-z]|\d|_)+([a-z]|\d|-|_)*)$/i, t('validationRoomUrlInvalidChars'))
        .max(128, t('validationMaxLength', { count: 128 })),
      welcomeMessage: Yup.string()
        .max(500, t('validationMaxLength', { count: 500 }))
        .nullable(),
      unavailableMessage: Yup.string()
        .max(100, t('validationMaxLength', { count: 100 }))
        .nullable(),
      preferedDisplayName: Yup.string()
        .max(50, t('validationMaxLength', { count: 50 }))
        .test('preferred-display-name-valid', t('validationPreferredDisplayNameInvalidChars'), (value) =>
          isPreferredDisplayNameValid(value),
        )
        .nullable()
        .label(t('preferredDisplayName')),
      biography: Yup.string()
        .max(2000, t('validationMaxLength', { count: 2000 }))
        .nullable(),
      displayEmail: Yup.boolean(),
      suffix: Yup.string().nullable(),
    }),
    user: Yup.object().shape({
      title: Yup.string().nullable(),
      speciality: Yup.string()
        .max(25, t('validationMaxLength', { count: 25 }))
        .nullable(),
    }),
  });

  const form = useForm<CreateWaitingRoomFormType>({
    resolver: yupResolver(schema),
    defaultValues: {},
    mode: 'all',
  });

  const [profilePicture, setProfilePicture] = useState<File | null>(null);

  const handleSubmit = async (): Promise<void> => {
    if (!form.formState.isValid) {
      return;
    }

    const formValues: CreateWaitingRoomFormType = form.getValues();

    await onSubmit(profilePicture, formValues.user, formValues.clinicUser).catch((error) => {
      enqueueSnackbar(error, { variant: 'error' });
    });
  };

  const handleSkip = (): void => {
    onSkip().catch((error) => {
      enqueueSnackbar(error, { variant: 'error' });
    });
  };

  // const handleBack = () => {
  //   onBack();
  // };

  return (
    <div className={styles.form}>
      <Stack className={styles.row}>
        <Typography
          component='p'
          variant='body4'
          color='var(--app-brand-primary)'
          onClick={handleSkip}
          className={styles.skipButton}
        >
          {t('createRoomSkipNow')}
        </Typography>
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('roomNameUrl')} <span className={styles.asterisk}>*</span>
          </Typography>
          <Info text={t('roomNameInfo')} />
        </Stack>
        <Stack direction='row' alignItems='center' className={styles.roomNameContainer}>
          <div className={styles.roomNameLink}>
            <Typography variant='body1' color='var(--app-text-muted)'>
              {window.location.origin}/
            </Typography>
          </div>
          <FormInput
            placeholder={t('roomNamePlaceholder')}
            name='clinicUser.roomName'
            control={form.control}
            className={styles.roomName}
            errorHelperClassName={styles.roomNameError}
          />
        </Stack>
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('welcomeMessage')}
          </Typography>
          <Info text={t('welcomeMessageInfo')} />
        </Stack>
        <FormInput
          name='clinicUser.welcomeMessage'
          control={form.control}
          multiline
          maxRows={4}
          placeholder={t(DEFAULT_WELCOME_MESSAGE)}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('unavailableMessage')}
          </Typography>
          <Info text={t('unavailableMessageInfo')} />
        </Stack>
        <FormInput
          name='clinicUser.unavailableMessage'
          control={form.control}
          multiline
          maxRows={4}
          placeholder={t(DEFAULT_UNAVAILABLE_MESSAGE)}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='flex-end'>
          <Typography component='p' variant='body2'>
            {t('profilePicture')}
          </Typography>
        </Stack>
        <FormMediaUpload
          onInputChange={(file) => setProfilePicture(file)}
          onRemove={() => setProfilePicture(null)}
          title={t('profilePicture')}
          src={user?.linkToProfilePicture ?? ''}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('titleLabel')}
          </Typography>
        </Stack>
        <FormSelector
          variant='outlined'
          name='user.title'
          control={form.control}
          options={getTitlesOptionsForUser()}
          MenuProps={titleSuffixMenuProps}
        />
      </Stack>

      {isHipaaLink && (
        <Stack className={styles.row}>
          <Stack direction='row' alignItems='center'>
            <Typography component='p' variant='body2'>
              {t('suffixLabel')}
            </Typography>
          </Stack>
          <FormSelector
            variant='outlined'
            name='clinicUser.suffix'
            control={form.control}
            options={getSuffixesOptionsForUser()}
            MenuProps={titleSuffixMenuProps}
          />
        </Stack>
      )}

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('preferredDisplayName')}
          </Typography>
          <Info text={t('preferredDisplayNameInfo')} />
        </Stack>
        <FormInput
          name='clinicUser.preferedDisplayName'
          control={form.control}
          errorHelperClassName={styles.errorHelper}
        />
      </Stack>

      <Stack className={styles.row}>
        <FormControlLabel
          control={
            <Checkbox
              className={styles.checkbox}
              {...form.register('clinicUser.displayEmail')}
              icon={<CheckboxIcon className={styles.checkboxIcon} />}
              checkedIcon={<CheckboxCheckedIcon className={styles.checkboxIcon} />}
            />
          }
          label={t('displayEmail')}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('personalBiography')}
          </Typography>
        </Stack>
        <FormInput name='clinicUser.biography' className={styles.bio} control={form.control} multiline maxRows={20} />
      </Stack>

      <Stack className={styles.row} direction='row' justifyContent='flex-end'>
        {/*
          hidden temporary
          <Stack direction='row' alignItems='center' sx={{ cursor: 'pointer' }} onClick={handleBack}>
            <ArrowBackIosIcon sx={{ width: '1rem' }} />
            <Typography variant='body2' color='#626363'>
              {isMobile ? 'Back' : 'Back to Personal Profile'}
            </Typography>
          </Stack> */}

        <Stack direction='row'>
          <Button variant='text' color='primary' onClick={handleSkip} className={styles.skipButtonBottom}>
            {t('skip')}
          </Button>

          <Button
            id='CreateRoomFormNext'
            variant='contained'
            onClick={handleSubmit}
            disabled={!form.formState.isValid}
            className={styles.formButton}
            type='submit'
          >
            {t('next')}
          </Button>
        </Stack>
      </Stack>
    </div>
  );
}
